<template>
  <div class="fl-picker">
    <div class="fl-picker__head">
      <span class="fl-picker__label">{{ label }}</span>
      <span class="fl-picker__count" v-if="selectedOptions.length">
        {{ selectedOptions.length }} selected
        <button type="button" class="fl-picker__clear" @click="clear">clear</button>
      </span>
    </div>

    <p class="fl-picker__hint" v-if="hint">{{ hint }}</p>

    <!-- Selected values stay visible while scrolling a long list, so it is always clear what has
         been picked without hunting for ticked boxes. -->
    <div class="fl-picker__chips" v-if="selectedOptions.length">
      <button
        type="button"
        class="fl-chip"
        v-for="option of selectedOptions"
        :key="'chip' + option.id"
        :title="'Remove ' + option.name"
        @click="toggle(option.id)"
      >
        <span>{{ option.name }}</span>
        <em aria-hidden="true">×</em>
      </button>
    </div>

    <div class="fl-picker__search" v-if="options.length > searchThreshold">
      <input
        type="text"
        v-model="query"
        :placeholder="'Search ' + options.length + ' options…'"
        :aria-label="'Search ' + label"
      >
      <button type="button" v-if="query" aria-label="Clear search" @click="query = ''">×</button>
    </div>

    <div
      class="fl-picker__options"
      :class="{ 'is-scrollable': visibleOptions.length > 8, 'is-grouped': isNested }"
      role="group"
      :aria-label="label"
    >
      <!-- Two-level taxonomies are grouped under their parent. Flat ones keep the plain grid:
           turning every value into its own one-item group would waste half the width. -->
      <template v-if="isNested">
        <div class="fl-picker__group" v-for="group of groupedOptions" :key="group.key">
          <!-- A parent is selectable in its own right, so it stays a real option and doubles as
               the heading. When the search hides it, its name is shown as a plain label so the
               matching children still say what they belong to. -->
          <button
            type="button"
            class="fl-option fl-option--parent"
            :class="{ 'is-on': isSelected(group.option.id) }"
            v-if="group.option"
            :aria-pressed="isSelected(group.option.id) ? 'true' : 'false'"
            @click="toggle(group.option.id)"
          >
            <i aria-hidden="true"></i>
            <span><b>{{ group.option.name }}</b></span>
          </button>
          <span class="fl-picker__grouplabel" v-else>{{ group.label }}</span>

          <div class="fl-picker__children" v-if="group.children.length">
            <button
              type="button"
              class="fl-option"
              :class="{ 'is-on': isSelected(option.id) }"
              v-for="option of group.children"
              :key="'opt' + option.id"
              :aria-pressed="isSelected(option.id) ? 'true' : 'false'"
              @click="toggle(option.id)"
            >
              <i aria-hidden="true"></i>
              <span><b>{{ option.name }}</b></span>
            </button>
          </div>
        </div>
      </template>

      <button
        type="button"
        class="fl-option"
        :class="{ 'is-on': isSelected(option.id) }"
        v-for="option of (isNested ? [] : visibleOptions)"
        :key="'opt' + option.id"
        :aria-pressed="isSelected(option.id) ? 'true' : 'false'"
        @click="toggle(option.id)"
      >
        <i aria-hidden="true"></i>
        <span><b>{{ option.name }}</b></span>
      </button>

      <p class="fl-picker__empty" v-if="!visibleOptions.length">
        {{ options.length ? 'Nothing matches “' + query + '”.' : emptyLabel }}
      </p>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'

  /**
   * Multi-select for one taxonomy: selected values as removable chips, a search box once the list
   * is long enough to need one, and toggle buttons rather than raw checkboxes.
   *
   * Lives beside the page rather than in components/ because it is written in the augmented
   * profile's own `fl-` design language. It carries its own styles — the page's `<style scoped>`
   * does not reach into a child component — but reads the `--fl-*` custom properties, which do
   * cascade from `.foodleap-actor-detail`.
   */
  export default defineComponent({
    name: 'FoodleapTaxonomyPicker',
    props: {
      /** Selected ids. Bound with v-model. */
      modelValue: {
        type: Array,
        default: () => [],
      },
      /** `[{ id, name, parent }]` — see the page's flattenTaxonomy. */
      options: {
        type: Array,
        default: () => [],
      },
      label: {
        type: String,
        required: true,
      },
      hint: {
        type: String,
        default: '',
      },
      emptyLabel: {
        type: String,
        default: 'Nothing is configured for this ecosystem yet.',
      },
      /** Above this many options the list gets a search box. */
      searchThreshold: {
        type: Number,
        default: 10,
      },
    },
    emits: ['update:modelValue'],
    data () {
      return {
        query: '',
      }
    },
    computed: {
      selectedIds () {
        return this.modelValue || []
      },
      selectedOptions () {
        // Driven off the option list so a stale id — a taxonomy value deleted since the actor was
        // classified — does not render a nameless chip.
        return this.options.filter(option => this.selectedIds.includes(option.id))
      },
      visibleOptions () {
        const query = this.query.trim().toLowerCase()

        if (!query) {
          return this.options
        }

        return this.options.filter(option => {
          const haystack = `${option.name} ${option.parent || ''}`.toLowerCase()

          return haystack.includes(query)
        })
      },
      /** Only worth grouping when something actually sits under a parent. */
      isNested () {
        return this.options.some(option => !!option.parent)
      },
      /**
       * The flat option list folded back into parent → children.
       *
       * Relies on the order the page flattens taxonomies in — a parent, then its own children.
       * A child whose parent was filtered out by the search still gets a heading, so it never
       * appears loose.
       */
      groupedOptions () {
        const groups = []
        let current = null

        for (const option of this.visibleOptions) {
          if (!option.parent) {
            current = { key: `g${option.id}`, label: option.name, option, children: [] }
            groups.push(current)

            continue
          }

          if (!current || current.label !== option.parent) {
            current = { key: `p${option.parent}-${option.id}`, label: option.parent, option: null, children: [] }
            groups.push(current)
          }

          current.children.push(option)
        }

        return groups
      },
    },
    methods: {
      isSelected (id) {
        return this.selectedIds.includes(id)
      },
      toggle (id) {
        const next = this.isSelected(id)
          ? this.selectedIds.filter(selected => selected !== id)
          : this.selectedIds.concat([id])

        this.$emit('update:modelValue', next)
      },
      clear () {
        this.$emit('update:modelValue', [])
      },
    },
  })
</script>

<style lang="scss" scoped>
  .fl-picker {
    margin-bottom: 24px;
  }

  .fl-picker__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .fl-picker__label {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--fl-muted, #6f7d78);
  }

  .fl-picker__count {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    font-size: 11px;
    color: var(--fl-muted, #6f7d78);
    white-space: nowrap;
  }

  /* Coral marks the things that take a selection away, which is the one place in the wizard where
     red carries meaning. The page's CTAs deliberately do not use it. */
  .fl-picker__clear {
    border: 0;
    background: none;
    padding: 0;
    font-size: 11px;
    color: var(--fl-coral, #ef374e);
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .fl-picker__hint {
    margin: 0 0 12px;
    font-size: 12px;
    line-height: 1.5;
    color: var(--fl-muted, #6f7d78);
  }

  .fl-picker__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .fl-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 8px 4px 12px;
    border: 1px solid var(--fl-green, #5cc61e);
    border-radius: 999px;
    background: var(--fl-mint, #eaf6e6);
    color: var(--fl-ink, #33413e);
    font-size: 12px;
    line-height: 1;
    cursor: pointer;

    em {
      font-style: normal;
      font-size: 14px;
      color: var(--fl-muted, #6f7d78);
    }

    &:hover {
      border-color: var(--fl-coral, #ef374e);

      em {
        color: var(--fl-coral, #ef374e);
      }
    }
  }

  .fl-picker__search {
    position: relative;
    margin-bottom: 8px;

    input {
      width: 100%;
      padding: 12px 32px 12px 12px;
      border: 1px solid var(--fl-line, #dfe8df);
      background: #fff;
      font-family: inherit;
      font-size: 13px;
      color: inherit;

      &:focus {
        outline: none;
        border-color: var(--fl-green, #5cc61e);
      }
    }

    button {
      position: absolute;
      top: 50%;
      right: 6px;
      transform: translateY(-50%);
      border: 0;
      background: none;
      font-size: 16px;
      line-height: 1;
      color: var(--fl-muted, #6f7d78);
      cursor: pointer;
    }
  }

  .fl-picker__options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;

    // Groups own their internal columns, so the container stacks them instead.
    &.is-grouped {
      grid-template-columns: 1fr;
      gap: 12px;
    }

    &.is-scrollable {
      max-height: 232px;
      overflow-y: auto;
      padding: 4px;
      border: 1px solid var(--fl-line, #dfe8df);
      background: var(--fl-pale, #f4f8f3);

      // Headings and group gaps make the same option count taller, so the box gets more room
      // before it starts scrolling.
      &.is-grouped {
        max-height: 340px;
      }
    }
  }

  .fl-picker__grouplabel {
    display: block;
    padding: 8px 12px 4px;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .08em;
    color: var(--fl-muted, #6f7d78);
  }

  .fl-picker__children {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;
    // Indented and ruled so the nesting is legible without repeating the parent on every child.
    margin-left: 12px;
    padding-left: 12px;
    border-left: 1px solid var(--fl-line, #dfe8df);
  }

  .fl-option--parent {
    font-weight: 500;

    b {
      font-weight: 500;
    }
  }

  .fl-option {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid transparent;
    background: #fff;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    i {
      flex: 0 0 14px;
      width: 14px;
      height: 14px;
      margin-top: 0;
      border: 1px solid var(--fl-line, #dfe8df);
      background: #fff;
    }

    b {
      display: block;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.35;
    }

    small {
      display: block;
      margin-top: 4px;
      font-size: 10px;
      color: var(--fl-muted, #6f7d78);
    }

    &:hover {
      border-color: var(--fl-green, #5cc61e);
    }

    &.is-on {
      border-color: var(--fl-green, #5cc61e);
      background: var(--fl-mint, #eaf6e6);

      i {
        border-color: var(--fl-green, #5cc61e);
        background: var(--fl-green, #5cc61e);
        // Tick drawn rather than an entity, so it scales with the box and needs no font support.
        background-image: linear-gradient(45deg, transparent 42%, #fff 42%, #fff 55%, transparent 55%),
          linear-gradient(-45deg, transparent 62%, #fff 62%, #fff 74%, transparent 74%);
      }
    }
  }

  .fl-picker__empty {
    grid-column: 1 / -1;
    margin: 0;
    padding: 12px;
    font-size: 12px;
    color: var(--fl-muted, #6f7d78);
  }

  @media (max-width: 900px) {
    .fl-picker__options,
    .fl-picker__children {
      grid-template-columns: 1fr;
    }
  }
</style>
